import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import facilityApi from '~/apis/facility';
import { CloseIcon } from '~/assets/icons';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import BaseRow from '~/components/common/base-row/BaseRow';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, DIALOG_MODE_VIEW } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import facilityValidation from '~/validation/facility';

type CheckReportDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData: boolean) => void;
  data?: any;
  locationId?: string;
  mode: string;
};

const initialValues: any = {
  status: '',
  check_date: '',
  inspector: '',
  note: '',
};

const CheckReportDialog = (props: CheckReportDialogProps) => {
  const { t } = useTranslation('admin/facility');

  const [timeType, setTimeType] = useState<string>('');
  const [checkStatus, setCheckStatus] = useState<number | null>();
  const isInitialRender = useRef(true);

  const handleChange = (value: number) => {
    setCheckStatus((prev) => (prev === value ? null : value));
  };

  const statusOptions = [
    {
      id: 0,
      name: t('detailFacilityDialog.listCheckReports.statusOptions.notChecked'),
    },
    {
      id: 1,
      name: t('detailFacilityDialog.listCheckReports.statusOptions.pass'),
    },
    {
      id: 2,
      name: t('detailFacilityDialog.listCheckReports.statusOptions.fail'),
    },
  ];
  const [statuses, setStatuses] = useState<any[]>(statusOptions);

  const form = useFormik({
    validateOnChange: false,
    validationSchema: facilityValidation(t).create,
    initialValues,
    onSubmit: async (values) => {
      const dataUpdate = {
        ...values,
        check_status: checkStatus,
        location_id: props.locationId,
      };

      if (checkStatus === null || checkStatus === undefined) {
        form.setFieldError('check_status', t('checkReportDialog.checkStatusRequired'));
        return;
      }

      if (props.mode === DIALOG_MODE_CREATE) {
        try {
          await facilityApi.addCheckReport(dataUpdate);
          notify('success', t('checkReportDialog.addSuccess'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }

      if (props.mode === DIALOG_MODE_EDIT) {
        try {
          await facilityApi.updateCheckReport(dataUpdate, props?.data.id);
          notify('success', t('checkReportDialog.editSuccess'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData: boolean) => {
    form.resetForm();
    if (!open) {
      setCheckStatus(null);
    }
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  useEffect(() => {
    let newStatuses = statusOptions;

    if (checkStatus === 0) {
      newStatuses = statusOptions.filter((s) => s.id === 0);
    } else if (checkStatus === 1) {
      newStatuses = statusOptions.filter((s) => s.id !== 0);
    }

    setStatuses(newStatuses);

    if (!newStatuses.some((s) => s.id === form.values.status)) {
      form.setFieldValue('status', '');
    }
  }, [checkStatus]);

  useEffect(() => {
    if (props.open && props.data && props.mode == DIALOG_MODE_EDIT) {
      setCheckStatus(props.data?.check_status);
      form.setValues({
        status: cast(props.data?.status),
        check_status: cast(props.data?.check_status),
        note: cast(props.data?.note),
        check_date: cast(props.data?.check_date),
        inspector: cast(props.data.inspector),
      });
    }
  }, [props.open]);

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false, false)} fullWidth maxWidth='sm'>
        <DialogTitle className='bg-[#005F73]'>
          {t('checkReportDialog.title')}
          <IconButton onClick={() => handleSetOpen(false, false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component='form'>
            <BaseRow className='gap-[20px] mb-[20px] !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel>{t('checkReportDialog.checkStatus')}</InputLabel>
                {' : '}
              </Box>
              <Box className='w-1/2 flex justify-end'>
                <Box className='flex flex-col items-end'>
                  <FormGroup row className='flex flex-row'>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkStatus === 1}
                          onChange={() => {
                            handleChange(1);
                            form.setFieldValue('check_status', 1);
                          }}
                        />
                      }
                      label={t('checkReportDialog.checked')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkStatus === 0}
                          onChange={() => {
                            handleChange(0);
                            form.setFieldValue('check_status', 0);
                          }}
                        />
                      }
                      label={t('checkReportDialog.skip')}
                    />
                  </FormGroup>
                  <Box className='flex w-full justify-start'>
                    {form.errors.check_status && (
                      <Box className='text-[#d32f2f] text-xs  text-right'>{form.errors.check_status as string}</Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </BaseRow>

            <BaseRow className='gap-[20px] mb-[20px] w-full'>
              <InputLabel className='w-1/2'>{t('checkReportDialog.status')}:</InputLabel>
              <Box className='w-1/2 flex justify-end'>
                <Box className='max-w-[235px] w-full'>
                  <BaseSelect
                    size='medium'
                    options={statuses}
                    value={form.values.status}
                    placeholder={t('select')}
                    name='status'
                    isSearchEnable
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.status)}
                    helperText={form.errors?.status as string}
                    displayInvalidValue
                  />
                </Box>
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <InputLabel className='w-1/2'>{t('checkReportDialog.checkDate')}:</InputLabel>
              <Box className='w-1/2 flex justify-end'>
                <Box className='max-w-[235px] w-full [&_.MuiInputBase-root]:h-[36px] [&_.MuiFormControl-root]:w-full'>
                  <BaseDatePicker
                    portalId='datePicker-portalId'
                    value={form.values.check_date}
                    dateFormat='YYYY-MM-DD'
                    onChange={(date) => {
                      form.setFieldValue('check_date', date);
                    }}
                    showYearPicker={timeType === 'year'}
                    showMonthYearPicker={timeType === 'month'}
                    placeholder={t('select')}
                  />
                  {form.errors.check_date && (
                    <Box className='text-[#d32f2f] text-xs mt-[10px]'>{form.errors.check_date as string}</Box>
                  )}
                </Box>
              </Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <InputLabel className='w-1/2'>{t('checkReportDialog.checkPerson')}:</InputLabel>
              <Box className='w-1/2 flex justify-end'>
                <Box className='max-w-[235px] w-full'>
                  <TextField
                    size='medium'
                    className='w-full ps-0'
                    name='inspector'
                    placeholder={t('input')}
                    value={form.values?.inspector}
                    onChange={form.handleChange}
                    error={Boolean(form.errors?.inspector)}
                  />
                </Box>
              </Box>
            </BaseRow>
            <Box className='gap-[20px] mb-[20px] flex flex-col'>
              <InputLabel className='w-1/2'>{t('checkReportDialog.note')}:</InputLabel>
              <Box className='w-full'>
                <TextField
                  size='medium'
                  className='w-full [&_.MuiInputBase-root]:h-auto  [&_.MuiOutlinedInput-root]:p-0 [&_.MuiOutlinedInput-root]:rounded-[8px] [&_textarea]:h-auto [&_textarea]:!p-[8px] rounded-[8px]'
                  name='note'
                  multiline
                  rows={2}
                  placeholder={t('input')}
                  value={form.values?.note}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.note)}
                />
              </Box>
            </Box>
            <Button
              variant='contained'
              color='secondary'
              className='min-w-[125px] flex ml-auto'
              onClick={() => {
                form.handleSubmit();
              }}
            >
              {t('done')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(CheckReportDialog);
